import React, { useState, useEffect } from 'react';
import { referralAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import {
  Users, Link2, Copy, Check, TrendingUp, DollarSign,
  MousePointerClick, UserCheck, Clock, ArrowUpRight, Gift,
  Trophy, Medal, Crown, Diamond
} from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const BADGE_CONFIG = {
  diamond: { label: 'Diamond', color: 'from-[#B9F2FF] to-[#62D4EE]', textColor: 'text-[#0C7C9E]', icon: <Diamond className="w-5 h-5" /> },
  gold: { label: 'Gold', color: 'from-[#FFE082] to-[#FFB300]', textColor: 'text-[#7A5600]', icon: <Crown className="w-5 h-5" /> },
  silver: { label: 'Silver', color: 'from-[#E0E0E0] to-[#9E9E9E]', textColor: 'text-[#424242]', icon: <Medal className="w-5 h-5" /> },
  bronze: { label: 'Bronze', color: 'from-[#FFCCBC] to-[#E65100]', textColor: 'text-[#BF360C]', icon: <Medal className="w-5 h-5" /> },
};

const ReferralDashboardPage = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [refLink, setRefLink] = useState('');
  const [refCode, setRefCode] = useState('');
  const [referrals, setReferrals] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [dashRes, linkRes, refRes, comRes] = await Promise.all([
          referralAPI.getDashboard(),
          referralAPI.getLink(),
          referralAPI.getReferrals(),
          referralAPI.getCommissions(),
        ]);
        setData(dashRes.data);
        setRefLink(linkRes.data.referral_link);
        setRefCode(linkRes.data.referral_code);
        setReferrals(refRes.data.referrals || []);
        setCommissions(comRes.data.commissions || []);
      } catch { toast.error('Failed to load referral data'); }
      finally { setLoading(false); }
    };
    fetchAll();
  }, []);

  const handleCopy = () => {
    navigator.clipboard.writeText(refLink);
    setCopied(true);
    toast.success('Referral link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin w-8 h-8 border-2 border-[#1A73E8] border-t-transparent rounded-full" />
    </div>
  );

  const d = data || {};
  const earn = d.earnings || {};
  const s = d.settings || {};

  return (
    <div className="space-y-6 animate-fade-in-up">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-gradient-to-br from-[#F9AB00] to-[#E37400] rounded-xl flex items-center justify-center">
            <Gift className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-[#202124]" style={{ fontFamily: 'Outfit' }}>Referral Program</h1>
            <p className="text-[#5F6368]">Earn commissions by referring new customers</p>
          </div>
          {d.badge && BADGE_CONFIG[d.badge] && (
            <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r ${BADGE_CONFIG[d.badge].color} ${BADGE_CONFIG[d.badge].textColor} text-sm font-semibold`} data-testid="user-badge">
              {BADGE_CONFIG[d.badge].icon} {BADGE_CONFIG[d.badge].label}
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          <Link to="/leaderboard" className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-[#FFB300] to-[#FF6F00] text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity" data-testid="go-to-leaderboard-btn">
            <Trophy className="w-4 h-4" /> Leaderboard
          </Link>
          <Link to="/dashboard/referrals/wallet" className="btn-google flex items-center gap-2" data-testid="go-to-wallet-btn">
            <DollarSign className="w-4 h-4" /> Wallet & Payouts
          </Link>
        </div>
      </div>

      {/* Badge Progress */}
      {d.badge_thresholds && (
        <div className="dashboard-card" data-testid="badge-progress">
          <h3 className="text-sm font-semibold text-[#202124] mb-3" style={{ fontFamily: 'Outfit' }}>
            <Trophy className="w-4 h-4 inline mr-1.5 text-[#F9AB00]" />Badge Progress
          </h3>
          <div className="flex items-center gap-2">
            {['bronze', 'silver', 'gold', 'diamond'].map(tier => {
              const threshold = d.badge_thresholds[tier] || 0;
              const reached = (d.total_referrals || 0) >= threshold;
              const cfg = BADGE_CONFIG[tier];
              return (
                <div key={tier} className={`flex-1 text-center p-2 rounded-lg border-2 transition-all ${reached ? `${cfg.border || 'border-[#DADCE0]'} bg-gradient-to-b ${cfg.color}` : 'border-[#E8EAED] bg-[#F8F9FA] opacity-50'}`}>
                  <div className="text-lg mb-0.5">{cfg.icon}</div>
                  <p className={`text-xs font-semibold ${reached ? cfg.textColor : 'text-[#9AA0A6]'}`}>{cfg.label}</p>
                  <p className="text-[10px] text-[#5F6368]">{threshold}+ refs</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Referral Link Card */}
      <div className="dashboard-card" data-testid="referral-link-card">
        <div className="flex items-center gap-3 mb-3">
          <Link2 className="w-5 h-5 text-[#1A73E8]" />
          <h3 className="text-base font-semibold text-[#202124]" style={{ fontFamily: 'Outfit' }}>Your Referral Link</h3>
        </div>
        <div className="flex items-center gap-2">
          <input
            value={refLink}
            readOnly
            className="input-google flex-1 bg-[#F8F9FA] text-sm"
            data-testid="referral-link-input"
          />
          <button
            onClick={handleCopy}
            className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
              copied ? 'bg-[#1E8E3E] text-white' : 'bg-[#1A73E8] text-white hover:bg-[#1557B0]'
            }`}
            data-testid="copy-link-btn"
          >
            {copied ? <><Check className="w-4 h-4" /> Copied</> : <><Copy className="w-4 h-4" /> Copy</>}
          </button>
        </div>
        <div className="mt-3 flex items-center gap-4 text-xs text-[#5F6368]">
          <span>Commission: <strong className="text-[#202124]">{s.one_time_commission_percent}%</strong> one-time</span>
          <span>+ <strong className="text-[#202124]">{s.recurring_commission_percent}%</strong> recurring</span>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="referral-stats">
        <StatCard icon={<Users className="w-5 h-5 text-[#1A73E8]" />} label="Total Referrals" value={d.total_referrals || 0} />
        <StatCard icon={<UserCheck className="w-5 h-5 text-[#1E8E3E]" />} label="Active Customers" value={d.active_customers || 0} />
        <StatCard icon={<MousePointerClick className="w-5 h-5 text-[#9334E9]" />} label="Clicks" value={d.clicks || 0} />
        <StatCard icon={<TrendingUp className="w-5 h-5 text-[#F9AB00]" />} label="Conversions" value={d.conversions || 0} />
      </div>

      {/* Earnings Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="earnings-summary">
        <div className="dashboard-card border-l-4 border-l-[#F9AB00]">
          <p className="text-xs text-[#5F6368] mb-1">Pending</p>
          <p className="text-lg font-semibold text-[#202124]">
            {earn.pending_inr > 0 && <span>₹{earn.pending_inr?.toFixed(2)}</span>}
            {earn.pending_inr > 0 && earn.pending_usd > 0 && <span className="text-[#5F6368] mx-1">·</span>}
            {earn.pending_usd > 0 && <span>${earn.pending_usd?.toFixed(2)}</span>}
            {!earn.pending_inr && !earn.pending_usd && <span>₹0.00</span>}
          </p>
        </div>
        <div className="dashboard-card border-l-4 border-l-[#1E8E3E]">
          <p className="text-xs text-[#5F6368] mb-1">Approved (Available)</p>
          <p className="text-lg font-semibold text-[#202124]">
            {earn.approved_inr > 0 && <span>₹{earn.approved_inr?.toFixed(2)}</span>}
            {earn.approved_inr > 0 && earn.approved_usd > 0 && <span className="text-[#5F6368] mx-1">·</span>}
            {earn.approved_usd > 0 && <span>${earn.approved_usd?.toFixed(2)}</span>}
            {!earn.approved_inr && !earn.approved_usd && <span>₹0.00</span>}
          </p>
        </div>
        <div className="dashboard-card border-l-4 border-l-[#1A73E8]">
          <p className="text-xs text-[#5F6368] mb-1">Total Paid</p>
          <p className="text-lg font-semibold text-[#202124]">
            {earn.paid_inr > 0 && <span>₹{earn.paid_inr?.toFixed(2)}</span>}
            {earn.paid_inr > 0 && earn.paid_usd > 0 && <span className="text-[#5F6368] mx-1">·</span>}
            {earn.paid_usd > 0 && <span>${earn.paid_usd?.toFixed(2)}</span>}
            {!earn.paid_inr && !earn.paid_usd && <span>₹0.00</span>}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="dashboard-card">
        <div className="flex gap-1 bg-[#F1F3F4] rounded-lg p-1 mb-4">
          {['overview', 'commissions'].map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors capitalize ${activeTab === tab ? 'bg-white text-[#202124] shadow-sm' : 'text-[#5F6368]'}`}
              data-testid={`tab-${tab}`}
            >{tab === 'overview' ? 'Referred Users' : 'Commissions'}</button>
          ))}
        </div>

        {activeTab === 'overview' && (
          referrals.length === 0 ? (
            <div className="text-center py-8">
              <Users className="w-10 h-10 text-[#DADCE0] mx-auto mb-2" />
              <p className="text-sm text-[#5F6368]">No referrals yet. Share your link to get started!</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="referrals-table">
                <thead>
                  <tr className="border-b border-[#DADCE0]">
                    <th className="text-left py-2 px-3 text-xs font-medium text-[#5F6368]">User</th>
                    <th className="text-left py-2 px-3 text-xs font-medium text-[#5F6368]">Plan</th>
                    <th className="text-left py-2 px-3 text-xs font-medium text-[#5F6368]">Status</th>
                    <th className="text-left py-2 px-3 text-xs font-medium text-[#5F6368]">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {referrals.map(r => (
                    <tr key={r.id} className="border-b border-[#F1F3F4]">
                      <td className="py-2.5 px-3">
                        <p className="font-medium text-[#202124]">{r.referred_name || r.referred_email}</p>
                        <p className="text-xs text-[#5F6368]">{r.referred_email}</p>
                      </td>
                      <td className="py-2.5 px-3 capitalize">{r.referred_plan}</td>
                      <td className="py-2.5 px-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          r.status === 'converted' ? 'bg-[#E6F4EA] text-[#1E8E3E]' : 'bg-[#FEF7E0] text-[#F9AB00]'
                        }`}>{r.status}</span>
                      </td>
                      <td className="py-2.5 px-3 text-[#5F6368]">{new Date(r.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}

        {activeTab === 'commissions' && (
          commissions.length === 0 ? (
            <div className="text-center py-8">
              <DollarSign className="w-10 h-10 text-[#DADCE0] mx-auto mb-2" />
              <p className="text-sm text-[#5F6368]">No commissions yet. Commissions are earned when referred users purchase a paid plan.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="commissions-table">
                <thead>
                  <tr className="border-b border-[#DADCE0]">
                    <th className="text-left py-2 px-3 text-xs font-medium text-[#5F6368]">Type</th>
                    <th className="text-left py-2 px-3 text-xs font-medium text-[#5F6368]">Plan</th>
                    <th className="text-left py-2 px-3 text-xs font-medium text-[#5F6368]">Amount</th>
                    <th className="text-left py-2 px-3 text-xs font-medium text-[#5F6368]">Status</th>
                    <th className="text-left py-2 px-3 text-xs font-medium text-[#5F6368]">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {commissions.map(c => (
                    <tr key={c.id} className="border-b border-[#F1F3F4]">
                      <td className="py-2.5 px-3 capitalize">{c.commission_type?.replace('_', ' ')}</td>
                      <td className="py-2.5 px-3 capitalize">{c.plan}</td>
                      <td className="py-2.5 px-3 font-medium">{c.currency === 'INR' ? '₹' : '$'}{c.commission_amount?.toFixed(2)}</td>
                      <td className="py-2.5 px-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          c.status === 'paid' ? 'bg-[#E8F0FE] text-[#1A73E8]' :
                          c.status === 'approved' ? 'bg-[#E6F4EA] text-[#1E8E3E]' :
                          'bg-[#FEF7E0] text-[#F9AB00]'
                        }`}>{c.status}</span>
                      </td>
                      <td className="py-2.5 px-3 text-[#5F6368]">{new Date(c.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>
    </div>
  );
};

const StatCard = ({ icon, label, value }) => (
  <div className="dashboard-card flex items-center gap-3">
    <div className="w-10 h-10 bg-[#F8F9FA] rounded-lg flex items-center justify-center flex-shrink-0">{icon}</div>
    <div>
      <p className="text-xs text-[#5F6368]">{label}</p>
      <p className="text-xl font-semibold text-[#202124]">{value}</p>
    </div>
  </div>
);

export default ReferralDashboardPage;
